import type { Event } from '../types';
import { getEventSourceKey } from '../utils/eventHelpers';
import { getEventSlug } from '../utils/eventRoutes';
import { browserProductAnalyticsTransport, ProductAnalyticsTransport } from './browserTransport';
import {
  AnalyticsPlatform,
  CalendarSource,
  ProductEventMap,
  ProductEventName,
  sanitizeProductEvent,
} from './contract';

let testTransport: ProductAnalyticsTransport | null = null;
const onceKeys = new Set<string>();
const MAX_ONCE_KEYS = 1000;

const platformSource = (): AnalyticsPlatform => {
  if (typeof window === 'undefined') return 'web';
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches
    || Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  return standalone ? 'pwa' : 'web';
};

export const normaliseCalendarSource = (value?: string): CalendarSource => {
  const direct = String(value || '').trim().toLowerCase();
  const source = ['shows', 'pokemon', 'onepiece', 'magic', 'riftbound', 'lorcana', 'fleshandblood'].includes(direct)
    ? direct
    : getEventSourceKey(value || '');
  return ['shows', 'pokemon', 'onepiece', 'magic', 'riftbound', 'lorcana', 'fleshandblood'].includes(source)
    ? source as CalendarSource
    : 'unknown';
};

export const getAnalyticsEventKey = (event: Event): string => getEventSlug(event).slice(0, 120);

export const getAnalyticsEventSource = (event: Event): CalendarSource => (
  normaliseCalendarSource(event.extendedProps?.sourceKey)
);

export const trackProductEvent = <Name extends ProductEventName>(
  name: Name,
  properties: ProductEventMap[Name],
): boolean => {
  const payload = sanitizeProductEvent(name, properties as unknown as Record<string, unknown>, platformSource());
  if (!payload) return false;

  try {
    (testTransport || browserProductAnalyticsTransport).send(name, payload);
    return true;
  } catch {
    return false;
  }
};

/** Render/effect dedupe only. Keys are recorded before transport to prevent pre-consent replay. */
export const trackProductEventOnce = <Name extends ProductEventName>(
  dedupeKey: string,
  name: Name,
  properties: ProductEventMap[Name],
): boolean => {
  const key = `${name}:${dedupeKey}`.slice(0, 240);
  if (onceKeys.has(key)) return false;
  if (onceKeys.size >= MAX_ONCE_KEYS) onceKeys.clear();
  onceKeys.add(key);
  return trackProductEvent(name, properties);
};

export const setProductAnalyticsTransportForTests = (transport: ProductAnalyticsTransport | null) => {
  testTransport = transport;
  onceKeys.clear();
};
